// Per-scope kernel pinning. Each scope "type" (overview / endpoints /
// sessions / clusters / …) gets its own list of kernel commands that
// should appear as tabs on the activity chart while the user is on
// any scope of that type.
//
// Scope key = the last dim in the active scopePath, or "overview"
// when there's no scope. So pinning a kernel while looking at
// endpoint X surfaces it on every endpoint page; pinning while on
// overview surfaces it on the global landing.
//
// localStorage shape: { "<scopeKey>": ["<command>", …] }
// CRUD + pub/sub; commands are stored verbatim — if the kernel they
// reference is later deleted, the pin is silently dropped on read.

import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

const STORAGE_KEY_PREFIX = "originDashboard.kernelPins.v2";
const LEGACY_STORAGE_KEY_PREFIX = "originDashboard.kernelPins.v1";

function storageKey(tenantId) {
    discardLegacyTenantEntry(globalThis.localStorage, LEGACY_STORAGE_KEY_PREFIX, tenantId);
    return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* noop */ }
    }
}

export function subscribePinning(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

function readMap(tenantId) {
    try {
        const key = storageKey(tenantId);
        if (!key) return {};
        const raw = localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function writeMap(tenantId, map) {
    try {
        const key = storageKey(tenantId);
        if (key) localStorage.setItem(key, JSON.stringify(map));
    } catch { /* noop */ }
    notify();
}

// Derive a scope's key from its scopePath. Identifies the scope TYPE
// (endpoints / sessions / clusters / …) rather than a specific entity
// so a single pin applies to every page of that type.
export function scopeKeyOf(scopePath) {
    if (!Array.isArray(scopePath) || !scopePath.length) return "overview";
    const last = scopePath[scopePath.length - 1];
    return last?.dim || "overview";
}

export function listPinnedCommands(scopeKey, tenantId) {
    const arr = readMap(tenantId)[scopeKey];
    return Array.isArray(arr) ? arr.slice() : [];
}

export function pinKernel(scopeKey, command, tenantId) {
    if (!scopeKey || !command) return;
    const map = readMap(tenantId);
    const cur = Array.isArray(map[scopeKey]) ? map[scopeKey] : [];
    if (cur.includes(command)) return;
    map[scopeKey] = [...cur, command];
    writeMap(tenantId, map);
}

export function unpinKernel(scopeKey, command, tenantId) {
    if (!scopeKey || !command) return;
    const map = readMap(tenantId);
    const cur = Array.isArray(map[scopeKey]) ? map[scopeKey] : [];
    const next = cur.filter((c) => c !== command);
    if (next.length === cur.length) return;
    if (next.length === 0) delete map[scopeKey];
    else map[scopeKey] = next;
    writeMap(tenantId, map);
}

export function isKernelPinned(scopeKey, command, tenantId) {
    return listPinnedCommands(scopeKey, tenantId).includes(command);
}

// Which kernels may mount on which scope surface:
//   • overview (root)  → both scope-agnostic ("global") and scope-
//     dependent ("scoped") kernels. A scoped kernel here runs over the
//     whole tenant (empty scope).
//   • any deeper scope → scope-dependent kernels only. A global kernel
//     would show the same fixed tenant-wide number on every page of the
//     type — noise — so it's confined to the overview.
// Per-scope-type lists are preserved (endpoints / clusters / … each keep
// their own pins); this only gates WHAT may go in a given list.
export function canMountOnScope(scopeMode, scopeKey) {
    if (scopeKey === "overview") return true;
    return scopeMode === "scoped";
}
